import { useEffect } from 'react';

import { ConfigURLPatternTypes } from '@omq/types';
import { useConfigContext } from '@omq/shared';

import type { URLChangeCallback, HelpURLParams } from '../stores/url-store';
import { URLStore } from '../stores/url-store';

type UpdateURLFuncType = (url: string, urlPatternType?: ConfigURLPatternTypes) => void;

const EMPTY_URL_PARAMS: HelpURLParams = {
  category: null,
  question: null,
  query: null,
  title: null,
};

/**
 * Hook to handle URL param changes.
 *
 * When config.isUrlSyncActive is false, URL integration is disabled entirely:
 * no pushState on updates, no popstate listener, and init-read returns empty state.
 *
 * @param {URLChangeCallback} changeHandler - callback handler for url param changes.
 *
 * @returns {[HelpURLParams, (params: HelpURLParams) => void]}
 */
export const useURLParams: (
  changeHandler?: URLChangeCallback,
) => [HelpURLParams, UpdateURLFuncType] = (changeHandler) => {
  const config = useConfigContext();
  const isUrlSyncActive = config.isUrlSyncActive;

  // read current values — empty state when URL sync is disabled
  const urlParams = isUrlSyncActive
    ? URLStore.readURLParams(config.urlPatternType)
    : EMPTY_URL_PARAMS;

  // add url change listener
  useEffect(() => {
    if (!isUrlSyncActive || changeHandler == null) {
      return;
    }

    // subscribe
    const urlChangeHandler = URLStore.registerURLParamChanges(changeHandler);

    // unsubscribe
    return () => {
      URLStore.unregisterURLParamChanges(urlChangeHandler);
    };
  }, [changeHandler, isUrlSyncActive]);

  // create update function
  const updateURL = (
    url: string,
    urlPatternType: ConfigURLPatternTypes = ConfigURLPatternTypes.QUERY_PARAM,
  ) => {
    if (!isUrlSyncActive) {
      return;
    }
    URLStore.writeURL(url, urlPatternType);
  };

  // return params & update function
  return [urlParams, updateURL];
};
